import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { fetchDeletedItems, restoreDeletedItem, permanentlyDeleteItem } from '../services/recycleBin.service';
import { EntityType, PagedDeletedItemResult, RecycleBinQuery } from '../types/softDelete.types';
import RecycleBinFilters from './components/RecycleBinFilters';
import DeletedItemsTable from './components/DeletedItemsTable';
import RestoreConfirmModal from './components/RestoreConfirmModal';
import PermanentDeleteConfirmModal from './components/PermanentDeleteConfirmModal';
import { validatePageSelection } from './components/recycleBinTableConfig';

interface LocalFilters {
  entityType?: EntityType;
  name?: string;
  clientName?: string;
  deletedBy?: string;
  from?: string;
  to?: string;
}

type BulkPrompt = { groupIds: string[]; selectedCount: number; operationCount: number };

interface Props {
  orgId: string | undefined;
  page: number;
  limit: number;
  onPageChange: (page: number, limit: number) => void;
  onOrgIdChange: (orgId: string | undefined) => void;
}

export default function RecordsTab({ orgId, page, limit, onPageChange, onOrgIdChange }: Props) {
  const [localFilters, setLocalFilters] = useState<LocalFilters>({});
  const [result, setResult] = useState<PagedDeletedItemResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [restorePrompt, setRestorePrompt] = useState<BulkPrompt | null>(null);
  const [permanentPrompt, setPermanentPrompt] = useState<BulkPrompt | null>(null);
  const [restoreProcessing, setRestoreProcessing] = useState(false);
  const [permanentProcessing, setPermanentProcessing] = useState(false);

  // The Name/Client/Deleted-By filters refetch on every keystroke with no debounce, so
  // several requests can be in flight at once. Without this, whichever one happens to
  // resolve LAST wins — including a broader, earlier-fired query that resolves after a
  // narrower, later one — silently reverting `result` (and the DataGrid's rows/selection
  // candidates) back to an unfiltered set the screen no longer shows as active.
  const fetchSeqRef = useRef(0);

  const fetchRecords = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (localFilters.entityType) params.set('entityType', localFilters.entityType);
      if (orgId) params.set('orgId', orgId);
      if (localFilters.name) params.set('name', localFilters.name);
      if (localFilters.clientName) params.set('clientName', localFilters.clientName);
      if (localFilters.deletedBy) params.set('deletedBy', localFilters.deletedBy);
      if (localFilters.from) params.set('from', localFilters.from);
      if (localFilters.to) params.set('to', localFilters.to);
      params.set('page', String(page));
      params.set('limit', String(limit));
      const data = (await fetchDeletedItems(params.toString())) as PagedDeletedItemResult;
      if (seq !== fetchSeqRef.current) return; // superseded by a newer request — discard
      setResult(data);
    } catch (e: unknown) {
      if (seq !== fetchSeqRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load recycle bin');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [orgId, page, limit, localFilters]);

  useEffect(() => { void fetchRecords(); }, [fetchRecords]);

  const handleFiltersChange = (f: RecycleBinQuery) => {
    const { orgId: newOrg, entityType, name, clientName, deletedBy, from, to } = f;
    if (newOrg !== orgId) onOrgIdChange(newOrg);
    setLocalFilters({ entityType, name, clientName, deletedBy, from, to });
  };

  const openBulkRestore = () => {
    const ids = selectionModel.map(String);
    const rows = validatePageSelection(ids, result?.data ?? [], (r) => r._id);
    if (!rows) {
      setError('Some checked rows are not on this page. Changing page clears the selection — select again on one page.');
      setSelectionModel([]);
      return;
    }
    const groupIds = [...new Set(rows.map((r) => r.groupId).filter(Boolean))];
    if (!groupIds.length) { setError('Nothing valid to restore for this selection.'); return; }
    setRestorePrompt({ groupIds, selectedCount: ids.length, operationCount: groupIds.length });
  };

  const openBulkPermanentDelete = () => {
    const ids = selectionModel.map(String);
    const rows = validatePageSelection(ids, result?.data ?? [], (r) => r._id);
    if (!rows) {
      setError('Some checked rows are not on this page. Changing page clears the selection — select again on one page.');
      setSelectionModel([]);
      return;
    }
    const groupIds = [...new Set(rows.map((r) => r.groupId).filter(Boolean))];
    if (!groupIds.length) { setError('Nothing valid to permanently delete for this selection.'); return; }
    setPermanentPrompt({ groupIds, selectedCount: ids.length, operationCount: groupIds.length });
  };

  const handleConfirmRestore = async () => {
    if (!restorePrompt) return;
    setRestoreProcessing(true);
    try {
      for (const gid of restorePrompt.groupIds) {
        await restoreDeletedItem(gid);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Restore failed');
      setRestoreProcessing(false);
      return;
    }
    // The restore(s) themselves already succeeded at this point — close the dialog and
    // stop the spinner now. The list refresh below is a courtesy, not a precondition: it
    // must never be able to leave the confirm dialog stuck on "Restoring…" if it's slow,
    // fails, or never settles.
    setRestorePrompt(null);
    setSelectionModel([]);
    setRestoreProcessing(false);
    fetchRecords().catch(() => {
      setError('Restore succeeded, but the list could not be refreshed — reload the page to see the latest state.');
    });
  };

  const handleConfirmPermanentDelete = async () => {
    if (!permanentPrompt) return;
    setPermanentProcessing(true);
    try {
      for (const gid of permanentPrompt.groupIds) {
        await permanentlyDeleteItem(gid);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Permanent delete failed');
      setPermanentProcessing(false);
      return;
    }
    setPermanentPrompt(null);
    setSelectionModel([]);
    setPermanentProcessing(false);
    fetchRecords().catch(() => {
      setError('Permanent delete succeeded, but the list could not be refreshed — reload the page to see the latest state.');
    });
  };

  const actionBusy = restoreProcessing || permanentProcessing;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <RecycleBinFilters
        filters={{ ...localFilters, orgId, page, limit }}
        onChange={handleFiltersChange}
        onApply={() => { setSelectionModel([]); void fetchRecords(); }}
        showEntityDateFilters
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectionModel.length > 0
            ? `${selectionModel.length} row(s) selected`
            : 'Use the checkboxes to choose rows, then Restore or Permanently delete'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            disabled={selectionModel.length === 0 || loading || actionBusy}
            onClick={openBulkRestore}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Restore selected{selectionModel.length > 0 ? ` (${selectionModel.length})` : ''}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={selectionModel.length === 0 || loading || actionBusy}
            onClick={openBulkPermanentDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Permanently delete{selectionModel.length > 0 ? ` (${selectionModel.length})` : ''}
          </Button>
        </Box>
      </Box>
      <DeletedItemsTable
        rows={result?.data ?? []}
        total={result?.total ?? 0}
        page={page}
        pageSize={limit}
        loading={loading}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setSelectionModel([]);
          const limitChanged = model.pageSize !== limit;
          onPageChange(limitChanged ? 1 : model.page + 1, model.pageSize);
        }}
      />
      {restorePrompt && (
        <RestoreConfirmModal
          open
          variant="bulkRecords"
          selectedCount={restorePrompt.selectedCount}
          operationCount={restorePrompt.operationCount}
          onConfirm={handleConfirmRestore}
          onClose={() => !restoreProcessing && setRestorePrompt(null)}
          isProcessing={restoreProcessing}
        />
      )}
      {permanentPrompt && (
        <PermanentDeleteConfirmModal
          open
          variant="bulkRecords"
          selectedCount={permanentPrompt.selectedCount}
          operationCount={permanentPrompt.operationCount}
          onConfirm={handleConfirmPermanentDelete}
          onClose={() => !permanentProcessing && setPermanentPrompt(null)}
          isProcessing={permanentProcessing}
        />
      )}
    </>
  );
}
